import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { getEnumValues } from '../../../../../../core/utilities';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { switchMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { getErrorKey, QuickDateTimeSelection } from '../../../../../global/gx-ag-grid/ag-date-time-filter/date-time-utils';
import { HttpErrorResponse } from '@angular/common/http';
import { LSCField } from '../../../../../../models/LSCFields';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../../../../../core/api-service';
import { FeedbackService } from '../../../../../../core/feedback.service';
import { LocationListApiService } from '../../../location-list-api.service';
import { Store } from '@ngrx/store';
import { selectAccountID } from '../../../../../../store/global/core-selectors';
import { subDays } from 'date-fns';
import { GxInputDirective } from '../../../../../../directives/gxinput.directive';
import { MatError, MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { MatDivider } from '@angular/material/divider';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatTimepicker, MatTimepickerInput, MatTimepickerToggle } from '@angular/material/timepicker';
import { MatButton } from '@angular/material/button';
import { RecentUpdateFilterLocalStoreService } from '../recent-update-filter-local-store.service';
import { Actions, ofType } from '@ngrx/effects';
import { LocationsLifecycleActions } from '../../../state/location-list-actions';
import { formatDate } from '../../../../../../core/utils/date-fns-utils';

@Component({
  selector: 'app-recent-update-filter',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    GxInputDirective,
    MatFormField,
    MatInput,
    MatSuffix,
    ReactiveFormsModule,
    MatChipSet,
    MatChip,
    MatDivider,
    MatDatepickerInput,
    MatDatepicker,
    MatDatepickerToggle,
    MatTimepickerInput,
    MatTimepicker,
    MatTimepickerToggle,
    MatButton,
    MatError,
    TranslateModule,
  ],
  templateUrl: './recent-update-filter.component.html',
  standalone: true,
  styleUrls: ['../../../../../global/gx-ag-grid/ag-date-time-filter/ag-date-time-filter.component.scss',
              './recent-update-filter.component.scss'],
})
export class RecentUpdateFilterComponent {

  protected now = new Date();
  protected readonly getErrorKey = getErrorKey;
  protected readonly quickDateTimeArr = getEnumValues(QuickDateTimeSelection);
  protected readonly filterState = new FormGroup<{
    fromDate: FormControl<Date>;
    fromTime: FormControl<Date>; }>
  ({
    fromDate: new FormControl(null, { validators: Validators.required }),
    fromTime: new FormControl(new Date(null,
        null,
        null,
        0,
        0)),
  })

  private readonly componentDestroyed = new Subject<void>();

  @Input() headerName: string;
  @Input() field: string;
  @Output() closeFilterMenu = new EventEmitter<void>();

  private readonly locationsComponentDestroyed$ =
      this.actions.pipe(ofType(LocationsLifecycleActions.locationsComponentDestroyed));

  constructor(private translate: TranslateService,
              private api: ApiService,
              private gridApi: LocationListApiService,
              private feedback: FeedbackService,
              private localStore: RecentUpdateFilterLocalStoreService,
              private store: Store,
              private actions: Actions) {
    this.filterState.valueChanges
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe(() => {
          this.now = new Date();
        });
  }

  /**
   * Causes location list filtering according to IDs of locations which had a respective field
   * updated in the past 24h.
   */
  protected getLocationsWithUpdates() {
    this.now = new Date();
    const dateTime = this.getDateTime();

    this.localStore.updateLoading(true);
    this.closeFilterMenu.emit();

    this.store.select(selectAccountID).pipe(
        switchMap((accountId) => this.api.getLocationIdsWithUpdates(accountId,
            this.field,
            dateTime.toISOString(),
            this.now.toISOString())),
        takeUntil(this.locationsComponentDestroyed$))
        .subscribe({
          next: res => {
            this.localStore.updateLoading(false);

            if (res.result?.length) {
              // set new model
              this.gridApi.setFilter(LSCField.id, res.result);
              // show column if hidden
              this.gridApi.toggleColumnVisibility(LSCField.id, true);
              // show success snackbar
              const format = 'dd.MM.yyyy, HH:mm';
              this.feedback.showSuccessSnackbar({
                data: {
                  userFriendlyMessage: `Location IDs for locations with ${this.headerName} updated in the time period since ${formatDate(dateTime, format)} until now have been filtered successfully.`
                },
                duration: 6000
              })
            } else {
              this.feedback.showInfoSnackbar({
                data: {
                  userFriendlyMessage: this.translate.instant(
                      'FILTER_LOCATIONS_BY_UPDATED_FIELD.SNACKBAR_NO_RESULT',
                      { field: this.headerName })
                },
                duration: 3000
              })
            }
          },
          error: (err: HttpErrorResponse) => {
            this.localStore.updateLoading(false);

            this.feedback.showAlertSnackbar({
              data: {
                userFriendlyMessage: this.translate.instant(
                    'FILTER_LOCATIONS_BY_UPDATED_FIELD.SNACKBAR_FAIL',
                    { field: this.headerName }),
                detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
              },
            }, err?.status);
          }
        });
  }

  /**
   * Set Model and dismiss filter popup.
   * @param selection
   */
  protected onQuickDateTimeClick(selection: QuickDateTimeSelection) {
    this.now = new Date();
    const fromDateFc = this.filterState.get('fromDate');
    const fromTimeFc = this.filterState.get('fromDate');
    let newControlValue: Date;

    switch (selection) {
      case QuickDateTimeSelection['24H']:
        newControlValue = subDays(this.now, 1);
        fromDateFc.setValue(newControlValue);
        fromTimeFc.setValue(newControlValue);
        break;
      case QuickDateTimeSelection['72H']:
        newControlValue = subDays(this.now, 3);
        fromDateFc.setValue(newControlValue);
        fromTimeFc.setValue(newControlValue);
        break;
      case QuickDateTimeSelection['7D']:
        newControlValue = subDays(this.now, 7);
        fromDateFc.setValue(newControlValue);
        fromTimeFc.setValue(newControlValue);
        break;
      case QuickDateTimeSelection['28D']:
        newControlValue = subDays(this.now, 28);
        fromDateFc.setValue(newControlValue);
        fromTimeFc.setValue(newControlValue);
        break;
      default: return;
    }

    this.getLocationsWithUpdates();
  }

  /**
   * Get max allowed timepicker value
   * @protected
   */
  protected getTimepickerMax() {
    const fromDateString = this.filterState.get('fromDate')?.value?.toDateString();
    if (fromDateString === this.now?.toDateString()) {
      return this.now?.toISOString();
    } else return undefined;
  }

  /**
   * Get a Date object consisting of both the datepicker and timepicker values.
   * @private
   */
  private getDateTime(): Date {
    const date = this.filterState?.get('fromDate')?.value;

    if (!date) {
      return null;
    }

    const time = this.filterState?.get('fromTime')?.value;
    const dateTime = new Date(date.getTime()); // clone Date object

    if (!time) {
      dateTime.setHours(0);
      dateTime.setMinutes(0);
    } else {
      dateTime.setHours(time.getHours());
      dateTime.setMinutes(time.getMinutes());
    }

    dateTime.setSeconds( 0);
    dateTime.setMilliseconds(0);

    return dateTime;
  }
}
